import api from '../../api/apiClient';
import { createResourceClient } from '../../api/resource';
import type { QuotationPdfFile } from '../admin/admin.api';

/**
 * Site visits (V27, `SiteVisitController`). The office schedules a visit with a checklist,
 * the engineer works it from the mobile app (results, remarks, photos, report), and the office
 * reviews it here. Everything sits under /api/site-visits.
 */

export type VisitStatus = 'SCHEDULED' | 'STARTED' | 'SUBMITTED' | 'REVIEWED' | 'CANCELLED';
export type VisitBy = 'SITE_ENGINEER' | 'DIRECTOR';
export type Shift = 'FIRST_HALF' | 'SECOND_HALF' | 'FULL_DAY';
export type CheckResult = 'OK' | 'ISSUE' | 'NA';
export type Outcome = 'OK_TO_PROCEED' | 'PROCEED_AFTER_CORRECTIONS' | 'NOT_OK_REVISIT';
export type FileKind = 'ATTACHMENT' | 'PHOTO';

export interface CodeLabel {
  code: string;
  label: string;
}

export interface EngineerOption {
  id: number;
  code: string | null;
  name: string;
  phone: string | null;
}

export interface ProjectOption {
  id: number;
  code: string | null;
  name: string | null;
  location: string | null;
  siteAddress: string | null;
  projectType: string | null;
  constructionWorkType: string | null;
  buildingType: string | null;
}

export interface TemplateItem {
  title: string;
  hint: string | null;
}

export interface Meta {
  engineers: EngineerOption[];
  projects: ProjectOption[];
  purposes: { purpose: string; items: TemplateItem[] }[];
  shifts: CodeLabel[];
  outcomes: CodeLabel[];
}

export interface VisitSummary {
  id: number;
  visitNo: string;
  visitSeq: number;
  jobId: number;
  projectCode: string | null;
  projectName: string | null;
  projectLocation: string | null;
  engineerId: number;
  engineerName: string | null;
  visitBy: VisitBy;
  purpose: string | null;
  stages: string | null;
  scheduledDate: string;
  shift: Shift;
  shiftLabel: string | null;
  status: VisitStatus;
  outcome: Outcome | null;
  outcomeLabel: string | null;
  checksTotal: number;
  checksDone: number;
  issues: number;
  photoCount: number;
  submittedAt: string | null;
}

export interface VisitFile {
  id: number;
  fileId: number;
  kind: FileKind;
  url: string;
  name: string | null;
  contentType: string | null;
  caption: string | null;
  checkId: number | null;
  createdAt: string;
}

export interface VisitCheck {
  id: number;
  sortOrder: number;
  stage: string | null;
  title: string;
  hint: string | null;
  result: CheckResult | null;
  remark: string | null;
  addedOnSite: boolean;
  photos: VisitFile[];
}

export interface VisitDetail extends VisitSummary {
  siteAddress: string | null;
  instructions: string | null;
  contacts: { role: string; name: string; phone: string | null }[];
  engineerPhone: string | null;
  startedAt: string | null;
  workStage: string | null;
  personsMet: string | null;
  observations: string | null;
  instructionsGiven: string | null;
  reviewNote: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  cancelReason: string | null;
  createdByName: string | null;
  createdAt: string;
  checks: VisitCheck[];
  attachments: VisitFile[];
  photos: VisitFile[];
  canEdit: boolean;
  canWork: boolean;
  canStart: boolean;
  canReview: boolean;
  canCancel: boolean;
}

export interface SaveVisitBody {
  jobId: number;
  engineerId: number;
  visitBy: VisitBy;
  purpose: string;
  stages?: string;
  scheduledDate: string;
  shift: Shift;
  instructions?: string;
  checks: { stage?: string | null; title: string; hint?: string | null }[];
  attachmentFileIds?: number[];
}

export interface ReportDraft {
  workStage?: string;
  personsMet?: string;
  observations?: string;
  instructionsGiven?: string;
  outcome?: Outcome | '';
}

export interface RegisterProject {
  jobId: number;
  projectCode: string | null;
  projectName: string | null;
  scheduled: number;
  completed: number;
  cancelled: number;
  engineerVisits: number;
  directorVisits: number;
  freeEngineerVisits: number | null;
  freeDirectorVisits: number | null;
  extraEngineerVisits: number;
  extraDirectorVisits: number;
  issues: number;
  lastVisitDate: string | null;
}

export interface RegisterEngineer {
  engineerId: number;
  engineerName: string | null;
  scheduled: number;
  completed: number;
  pending: number;
  issues: number;
}

export interface Register {
  from: string | null;
  to: string | null;
  projects: RegisterProject[];
  engineers: RegisterEngineer[];
}

export interface ChecklistTemplate {
  id: number;
  purpose: string;
  title: string;
  hint: string | null;
  sortOrder: number;
  active: boolean;
}

export interface VisitFilters {
  status?: VisitStatus | '';
  engineerId?: number | '';
  jobId?: number | '';
  from?: string;
  to?: string;
}

const BASE = '/api/site-visits';

async function data<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  return (await p).data.data;
}

/** Drops empty filters so the query string carries only what was chosen. */
const params = (o: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined));

export const siteVisitsApi = {
  meta: () => data<Meta>(api.get(`${BASE}/meta`)),
  list: (f: VisitFilters) => data<VisitSummary[]>(api.get(BASE, { params: params({ ...f }) })),
  get: (id: number | string) => data<VisitDetail>(api.get(`${BASE}/${id}`)),
  create: (body: SaveVisitBody) => data<VisitDetail>(api.post(BASE, body)),
  update: (id: number, body: SaveVisitBody) => data<VisitDetail>(api.put(`${BASE}/${id}`, body)),
  cancel: (id: number, reason: string) => data<VisitDetail>(api.post(`${BASE}/${id}/cancel`, { reason })),
  start: (id: number) => data<VisitDetail>(api.post(`${BASE}/${id}/start`)),
  updateCheck: (id: number, checkId: number, body: { result: CheckResult | null; remark: string | null }) =>
    data<VisitCheck>(api.put(`${BASE}/${id}/checks/${checkId}`, body)),
  addCheck: (id: number, body: { title: string; result?: CheckResult | null; remark?: string | null }) =>
    data<VisitCheck>(api.post(`${BASE}/${id}/checks`, body)),
  deleteCheck: (id: number, checkId: number) => data<null>(api.delete(`${BASE}/${id}/checks/${checkId}`)),
  addFile: (id: number, body: { fileId: number; kind: FileKind; caption?: string; checkId?: number | null }) =>
    data<VisitFile>(api.post(`${BASE}/${id}/files`, body)),
  updateFile: (id: number, rowId: number, caption: string) =>
    data<VisitFile>(api.put(`${BASE}/${id}/files/${rowId}`, { caption })),
  deleteFile: (id: number, rowId: number) => data<null>(api.delete(`${BASE}/${id}/files/${rowId}`)),
  saveReport: (id: number, body: ReportDraft) => data<VisitDetail>(api.put(`${BASE}/${id}/report`, body)),
  submit: (id: number, body: ReportDraft) => data<VisitDetail>(api.post(`${BASE}/${id}/submit`, body)),
  review: (id: number, note?: string) => data<VisitDetail>(api.post(`${BASE}/${id}/review`, { note })),
  sendBack: (id: number, note: string) => data<VisitDetail>(api.post(`${BASE}/${id}/send-back`, { note })),
  register: (f: { from?: string; to?: string }) => data<Register>(api.get(`${BASE}/register`, { params: params(f) })),
};

/** Checklist templates — the uniform CRUD shape, so ResourceManager drives it. */
export const checklistTemplatesApi = createResourceClient<ChecklistTemplate>(`${BASE}/templates`);

/** The visit report PDF as an object URL for the viewer; the caller revokes it. */
export async function fetchVisitPdf(id: number, visitNo: string): Promise<QuotationPdfFile> {
  const res = await api.get<Blob>(`${BASE}/${id}/pdf`, { responseType: 'blob' }).catch(async (error: any) => {
    // A failed PDF arrives as a Blob too; read its JSON message so the toast says what went wrong.
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        error.userMessage = parsed?.message || parsed?.error || error.userMessage;
      } catch {
        // not JSON — keep the interceptor's message
      }
    }
    throw error;
  });
  return {
    url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })),
    filename: `SiteVisit-${visitNo.replace(/[^A-Za-z0-9._-]+/g, '-')}.pdf`,
  };
}

export const STATUS_LABELS: Record<VisitStatus, string> = {
  SCHEDULED: 'Scheduled',
  STARTED: 'In progress',
  SUBMITTED: 'Submitted',
  REVIEWED: 'Reviewed',
  CANCELLED: 'Cancelled',
};

export const STATUS_COLORS: Record<VisitStatus, 'default' | 'info' | 'warning' | 'success' | 'error' | 'primary'> = {
  SCHEDULED: 'info',
  STARTED: 'warning',
  SUBMITTED: 'primary',
  REVIEWED: 'success',
  CANCELLED: 'default',
};

export const VISIT_BY_LABELS: Record<VisitBy, string> = {
  SITE_ENGINEER: 'Site engineer',
  DIRECTOR: 'Director',
};

/** Shown until /meta answers, and as the fallback when it lacks a code. */
export const SHIFTS: CodeLabel[] = [
  { code: 'FIRST_HALF', label: 'First half' },
  { code: 'SECOND_HALF', label: 'Second half' },
  { code: 'FULL_DAY', label: 'Full day' },
];

export const OUTCOMES: CodeLabel[] = [
  { code: 'OK_TO_PROCEED', label: 'OK to proceed' },
  { code: 'PROCEED_AFTER_CORRECTIONS', label: 'Proceed after corrections' },
  { code: 'NOT_OK_REVISIT', label: 'Not OK – revisit needed' },
];

export const outcomeColor = (o: Outcome | null | undefined): 'success' | 'warning' | 'error' | 'default' =>
  o === 'OK_TO_PROCEED' ? 'success' : o === 'PROCEED_AFTER_CORRECTIONS' ? 'warning' : o === 'NOT_OK_REVISIT' ? 'error' : 'default';

/** "27-010-N2 - THE URBANA PROJECT, Kothrud" */
export const projectLabel = (code?: string | null, name?: string | null, location?: string | null) =>
  `${[code, name].filter(Boolean).join(' - ')}${location ? `, ${location}` : ''}`;

export const isImage = (f: Pick<VisitFile, 'contentType' | 'name' | 'url'>) =>
  (f.contentType ?? '').startsWith('image/') || /\.(jpe?g|png|gif|webp|heic)$/i.test(f.name ?? f.url ?? '');
